import { useEffect, useState } from 'react';
import { Box, Typography, Paper, Stack, Button, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { api } from '../services/api';

const STATUSES = ['', 'Open', 'InProgress', 'WaitingOnCustomer', 'Resolved', 'Closed'];
const PRI_COLOR = { Critical: 'error', High: 'warning', Normal: 'info', Low: 'default' };

export default function TicketsPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [] });
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const load = () => api.get('/api/tickets', { params: { status: status || undefined, search } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Tickets</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>New Ticket</Button>
      </Stack>
      <Paper sx={{ p: 2, mb: 2 }}>
        <form onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} size="small" sx={{ minWidth: 180 }}>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s || 'All'}</MenuItem>)}
            </TextField>
            <TextField fullWidth placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
            <Button type="submit">Search</Button>
          </Stack>
        </form>
      </Paper>
      <Paper>
        <Table>
          <TableHead><TableRow>
            <TableCell>Number</TableCell><TableCell>Title</TableCell><TableCell>Status</TableCell>
            <TableCell>Priority</TableCell><TableCell>Assigned</TableCell><TableCell>Updated</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.items.map(t => (
              <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => nav(`/tickets/${t.id}`)}>
                <TableCell>{t.ticketNumber}</TableCell>
                <TableCell>{t.title}</TableCell>
                <TableCell><Chip size="small" label={t.status} /></TableCell>
                <TableCell><Chip size="small" label={t.priority} color={PRI_COLOR[t.priority] || 'default'} /></TableCell>
                <TableCell>{t.assignedToName || '—'}</TableCell>
                <TableCell>{new Date(t.updatedAt || t.createdAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={6} align="center">No tickets</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <CreateTicketDialog open={open} onClose={() => setOpen(false)} onCreated={(t) => { setOpen(false); nav(`/tickets/${t.id}`); }} />
    </Box>
  );
}

function CreateTicketDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ title: '', description: '', priority: 'Normal', category: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post('/api/tickets', f); onCreated(data); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New ticket</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <TextField label="Description" multiline minRows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <TextField select label="Priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            {['Low', 'Normal', 'High', 'Critical'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField label="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !f.title}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
